import type {
    FilteredItemsPageUrlQueryParams,
    ItemsCategoryPropertyFiltersFormValues,
    ItemsListRouteParams,
} from '@frontend/modules/public/ads/AdsModuleTypes'
import {
    type MouseEvent,
    type SubmitEvent,
    useEffect,
    useRef,
    useState,
} from 'react'
import {useParams} from 'react-router-dom'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import {useUrlQueryParams} from 'swayok-react-mdb-ui-kit/helpers/useUrlQueryParams'
import type {ReactSetState} from 'swayok-react-mdb-ui-kit/types'
import {decodeItemsSearchFiltersFromUrlQueryParam} from '../../../../helpers/decodeItemsSearchFiltersFromUrlQueryParam'
import {extractIdFromUrlSlug} from '../../../../helpers/extractIdFromUrlSlug'
import {makeObjectHash} from '../../../../helpers/makeObjectHash'
import {modifyItemsSearchUrlQueryParams} from '../../../../helpers/modifyItemsSearchUrlQueryParams'
import type {CommunicationMethodFilterValue} from '../components/category_filters/filters/CommunicationMethodFilter'

export interface UseAdCategoryFiltersFormHookReturn {
    filters: ItemsCategoryPropertyFiltersFormValues
    setFilters: ReactSetState<ItemsCategoryPropertyFiltersFormValues>
    isSubmitting: boolean
    onSubmit: (event?: MouseEvent<HTMLElement> | SubmitEvent<HTMLElement>) => void
    resetFromUrl: () => void
    urlQueryData: FilteredItemsPageUrlQueryParams
    categoryId: number | null
}

// Значения фильтров объявлений, синхронизированные с URL query, и их применение.
export function useAdCategoryFiltersForm(
    onSubmitted?: () => void
): UseAdCategoryFiltersFormHookReturn {

    const {
        setUrlQueryParams,
        urlQueryData,
    } = useUrlQueryParams<FilteredItemsPageUrlQueryParams>()

    // Фильтры.
    const [
        filters,
        setFilters,
    ] = useState<ItemsCategoryPropertyFiltersFormValues>({})

    // Предотвращение спама.
    const [
        isSubmitting,
        setIsSubmitting,
    ] = useState<boolean>(false)

    // Хэш предыдущих параметров URL query.
    const prevUrlQueryHashRef = useRef<string | null>(null)

    // Данные о текущем URL.
    const routeParams = useParams<ItemsListRouteParams>()
    const categoryId: number | null = extractIdFromUrlSlug(routeParams.slugWithId)

    // Обновление состояний при изменении соответствующих параметров в URL query.
    useEffect(() => {
        const newHash: string = makeObjectHash(urlQueryData)
        if (newHash === prevUrlQueryHashRef.current) {
            // Нет изменений в URL query параметрах.
            return
        }
        const standaloneFilters: Partial<ItemsCategoryPropertyFiltersFormValues> = {
            minPrice: normalizePrice(urlQueryData.minPrice),
            maxPrice: normalizePrice(urlQueryData.maxPrice),
            communicationMethod: normalizeCommunicationMethod(urlQueryData.cm),
        }
        decodeItemsSearchFiltersFromUrlQueryParam(urlQueryData.f)
            .then(props => {
                setFilters(filters => ({
                    ...filters,
                    ...standaloneFilters,
                    props,
                }))
            })
            .catch(() => {
                // Не выводим и не логируем ошибку т.к. могут заспамить кривыми запросами.
                setFilters(filters => ({
                    ...filters,
                    ...standaloneFilters,
                    props: {},
                }))
            })
        prevUrlQueryHashRef.current = newHash
    }, [urlQueryData.minPrice, urlQueryData.maxPrice, urlQueryData.cm, urlQueryData.f])

    // Отменить изменения, которые еще не применены.
    const resetFromUrl = useEventCallback(() => {
        const standaloneFilters: Partial<ItemsCategoryPropertyFiltersFormValues> = {
            minPrice: normalizePrice(urlQueryData.minPrice),
            maxPrice: normalizePrice(urlQueryData.maxPrice),
            communicationMethod: normalizeCommunicationMethod(urlQueryData.cm),
        }
        decodeItemsSearchFiltersFromUrlQueryParam(urlQueryData.f)
            .then(props => {
                setFilters({
                    ...standaloneFilters,
                    props,
                })
            })
            .catch(() => {
                // Не выводим и не логируем ошибку т.к. могут заспамить кривыми запросами.
                setFilters(filters => ({
                    ...filters,
                    ...standaloneFilters,
                    props: {},
                }))
            })
    })

    // Применение фильтров.
    const onSubmit = useEventCallback((
        event?: MouseEvent<HTMLElement> | SubmitEvent<HTMLElement>
    ) => {
        event?.preventDefault()
        onSubmitted?.()
        if (isSubmitting) {
            return
        }
        // Блокировка повторных запросов на небольшое время, чтобы предотвратить спам.
        setIsSubmitting(true)
        setTimeout(() => setIsSubmitting(false), 1500)

        modifyItemsSearchUrlQueryParams(
            urlQueryData,
            {},
            {
                ...filters,
                // Выбранные оба способа связи дают тот же результат, что и фильтр без значения.
                communicationMethod: filters.communicationMethod?.length === 1
                    ? filters.communicationMethod
                    : undefined,
            }
        )
            .then(newQueryParams => {
                prevUrlQueryHashRef.current = makeObjectHash(newQueryParams)
                setUrlQueryParams(new URLSearchParams(newQueryParams))
            })
            .catch(e => {
                console.error(e)
            })
    })

    return {
        filters,
        setFilters,
        isSubmitting,
        onSubmit,
        resetFromUrl,
        urlQueryData,
        categoryId,
    }
}

// Нормализация цены (если значение получено из URL query).
function normalizePrice(price?: string | null): number | null {
    if (!price || price.trim().length === 0) {
        return null
    }
    const value = parseInt(price.trim())
    if (isNaN(value)) {
        return null
    }
    return value
}

// Нормализация способа связи (если значение получено из URL query).
function normalizeCommunicationMethod(
    value?: string | CommunicationMethodFilterValue | null
): CommunicationMethodFilterValue {
    return value === 'phone' || value === 'messages' ? [value] : []
}
